/**
 * VoiceAssistantContext — coordinates the single global voice assistant with the
 * per-page workflow assistants, and hosts a shared ACTION REGISTRY.
 *
 * Slot coordination: pages that mount their own specialized VoiceAssistantUI
 * "acquire a slot" while mounted; the global assistant hides itself whenever a
 * slot is held, so the user never sees two floating mic buttons.
 *
 * Action registry: any page can register named actions the global assistant can
 * trigger by voice (e.g. "download set 5"). Each action:
 *   { name, description, params?: [{name,type,description}], handler(params),
 *     confirm?: boolean, undoable?: boolean }
 * handler may return { undo: fn, message?: string } to enable "undo".
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const VoiceSlotContext = createContext({
  pageAssistantActive: false,
  acquireSlot: () => {},
  releaseSlot: () => {},
  registerActions: () => () => {},
  getAction: () => undefined,
  listActions: () => [],
  actionsVersion: 0,
});

export function VoiceAssistantProvider({ children }) {
  const [count, setCount] = useState(0);
  const acquireSlot = useCallback(() => setCount((c) => c + 1), []);
  const releaseSlot = useCallback(() => setCount((c) => Math.max(0, c - 1)), []);

  // Action registry (ref so registrations don't re-render; version bumps notify).
  const actionsRef = useRef(new Map());
  const [actionsVersion, setActionsVersion] = useState(0);

  const registerActions = useCallback((list) => {
    const items = Array.isArray(list) ? list : [];
    items.forEach((a) => a && a.name && actionsRef.current.set(a.name, a));
    setActionsVersion((v) => v + 1);
    return () => {
      items.forEach((a) => a && a.name && actionsRef.current.delete(a.name));
      setActionsVersion((v) => v + 1);
    };
  }, []);

  const getAction = useCallback((name) => actionsRef.current.get(name), []);
  const listActions = useCallback(() => Array.from(actionsRef.current.values()), []);

  return (
    <VoiceSlotContext.Provider
      value={{
        pageAssistantActive: count > 0,
        acquireSlot,
        releaseSlot,
        registerActions,
        getAction,
        listActions,
        actionsVersion,
      }}
    >
      {children}
    </VoiceSlotContext.Provider>
  );
}

export const useVoiceSlot = () => useContext(VoiceSlotContext);

/**
 * Call from a page that mounts its own workflow VoiceAssistantUI. While the page
 * is mounted, the global assistant is suppressed.
 */
export function usePageVoiceSlot(active = true) {
  const { acquireSlot, releaseSlot } = useVoiceSlot();
  useEffect(() => {
    if (!active) return undefined;
    acquireSlot();
    return releaseSlot;
  }, [acquireSlot, releaseSlot, active]);
}

/**
 * Register voice actions for the current page. Pass a STABLE array (memoize with
 * useMemo/useCallback handlers) so it isn't re-registered every render.
 */
export function useVoiceActions(actionList) {
  const { registerActions } = useVoiceSlot();
  useEffect(() => {
    if (!actionList || !actionList.length) return undefined;
    return registerActions(actionList);
  }, [registerActions, actionList]);
}
